import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { toast as _sonnerToast } from "sonner";
import { formatApiError } from "./lib/errors";

// =============================================================================
// v1.0.5 — PERMANENT GLOBAL TOAST SAFETY NET
// -----------------------------------------------------------------------------
// React minified error #31 ("Objects are not valid as a React child") was
// surfacing on the register page whenever a 422 response containing an array
// `detail` was passed to `toast.error(rawDetail)`. We fixed every known call
// site in v1.0.4, BUT new code or third-party paths could regress this any
// time. The bullet-proof remedy is to wrap sonner's toast functions ONCE at
// the application root so EVERY existing import of `toast` (50+ call sites)
// gets the safety net automatically — no code change required at the call
// sites, including those we don't own (e.g. the axios interceptor).
//
// `formatApiError` ALWAYS returns a guaranteed string. Wrapping here is
// idempotent — the import-side cache holds the same `toast` reference for
// every consumer, so this single block protects the entire app.
// =============================================================================
if (!_sonnerToast.__rudras_safe_wrapped) {
  const _wrap = (fn) => (msg, opts) => {
    // Preserve intentional React elements / JSX (some apps pass <div/> to toast).
    if (msg && typeof msg === "object" && msg.$$typeof) return fn(msg, opts);
    return fn(formatApiError(msg, ""), opts);
  };
  ["error", "message", "success", "warning", "info"].forEach((k) => {
    const orig = _sonnerToast[k];
    if (typeof orig === "function") _sonnerToast[k] = _wrap(orig);
  });
  _sonnerToast.__rudras_safe_wrapped = true;
}
import LandingPage from "./pages/LandingPage";
import NotFoundPage from "./pages/NotFoundPage";
import { LoginPage, RegisterPage, ResetPasswordPage } from "./pages/AuthPages";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import { Overview, BusinessProfile, ChatbotSettings, Resources, Leads, Conversations, UsageBilling, Subscription, ShareLink, Privacy, EmailLogs, CustomerSecurity, CustomerTeam, EmbedWidget } from "./pages/Dashboard";
import PublicChatbot from "./pages/PublicChatbot";
import ShowcasePage from "./pages/ShowcasePage";
import RetailCustomerPage from "./pages/RetailCustomer";
import RetailChatPage from "./pages/RetailChat";
import RetailOrderStatusPage from "./pages/RetailOrderStatus";
import RetailPaymentReturn from "./pages/RetailPaymentReturn";
import RetailAppReturn from "./pages/RetailAppReturn";
import RetailDeleteMyData from "./pages/RetailDeleteMyData";
import RetailOrdersAdminPage from "./pages/RetailOrdersAdmin";
import KitchenDisplayPage from "./pages/KitchenDisplay";
import RetailCatalogsAdminPage from "./pages/RetailCatalogsAdmin";
import RetailLinkProfilesAdminPage from "./pages/RetailLinkProfilesAdmin";
import IcpLibraryAdminPage from "./pages/IcpLibraryAdmin";
import { CustomerListPage, CustomerProfilePage } from "./pages/Customers";
import { RetailReportsPage, RetailReportViewPage } from "./pages/RetailReports";
import CommerceSettingsPage from "./pages/CommerceSettings";
import CloverLaunchPage from "./pages/CloverLaunchPage";
import ShippingAdminPage from "./pages/ShippingAdmin";
import LocalDeliveryAdminPage from "./pages/LocalDeliveryAdmin";
import ParcelShippingSettingsPage from "./pages/ParcelShippingSettings";
import InventoryAdminPage from "./pages/InventoryAdmin";
import MessagingAdminPage from "./pages/MessagingAdmin";
import PrintingAdminPage from "./pages/PrintingAdmin";
import { FeaturesPage, PricingPage, RestaurantPage, HealthcarePage } from "./pages/MarketingPages";
// Twilio A2P 10DLC compliance — dedicated public pages at canonical URLs.
import {
  PrivacyPolicyPage, TermsOfServicePage, SmsProgramPage, ContactPage,
} from "./pages/CompliancePages";
import DemoCreatePage from "./pages/DemoCreatePage";
import { InternalLoginPage, InternalResetPasswordPage, InternalOverview, InternalTenants, InternalLeads, InternalSessions, InternalBrainTrace, InternalDemoJobs, InternalEmailLogs, InternalSecurity, InternalEscalations, InternalTestChecklist, InternalProtected, isInternalRole, InternalOtpDiagnostics } from "./pages/InternalAdmin";
import InternalOperationsConsole from "./pages/InternalOperationsConsole";
import InternalDiagnosticsPage from "./pages/InternalDiagnosticsPage";
import InternalUberTestDispatchConsole from "./pages/InternalUberTestDispatchConsole";
import InternalUberProductionOnboarding from "./pages/InternalUberProductionOnboarding";
import InternalMasterProductImages from "./pages/InternalMasterProductImages";
import HostGuestLanding from "./pages/hospitality/HostGuestLanding";
import { HostLoginPage, HostDashboardPage, HostOrdersPage, HostOrderDetailPage, HostSummaryPage } from "./pages/hospitality/HostPortal";
import { isHostRole } from "./lib/hostPortal";
import SessionExpiredModal from "./components/SessionExpiredModal";
import AuthErrorBoundary from "./components/AuthErrorBoundary";

function Protected({ children, role }) {
  const { user, loading, sessionExpired } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  // Stabilization phase: if the session just expired mid-page, KEEP the
  // current children mounted so the SessionExpiredModal can overlay them.
  // The modal owns the redirect to /login?next=... when the user accepts.
  // Without this branch the Protected route would immediately bounce to
  // /login and the modal would never get a chance to show.
  if (!user) {
    if (sessionExpired) return children;
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/login?next=${next}`} replace/>;
  }
  // Internal admin accounts MUST use the internal portal — never the customer dashboard.
  if (isInternalRole(user.role)) return <Navigate to="/internal-admin" replace/>;
  // Hospitality H1 — a hotel/motel Host Partner account never enters a
  // restaurant screen. (The backend refuses host tokens on every
  // restaurant route too; this keeps the UI from even trying.)
  if (isHostRole(user.role)) return <Navigate to="/host" replace/>;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace/>;
  return children;
}

// Customer portal alias: /app/* mirrors /dashboard/* for forward-compat with the
// target app.rudras-ai.com URL structure. We keep /dashboard live to preserve
// existing customer bookmarks and avoid regressing tests.
function AppAliasRedirect() {
  const loc = useLocation();
  const target = loc.pathname.replace(/^\/app/, "/dashboard") + loc.search + loc.hash;
  return <Navigate to={target} replace/>;
}

// /super → /internal-admin/tenants (legacy back-compat).
function SuperLegacyRedirect() {
  return <Navigate to="/internal-admin/tenants" replace/>;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AuthErrorBoundary><Toaster richColors closeButton position="top-right"/></AuthErrorBoundary>
        <SessionExpiredModal />
        <Routes>
          <Route path="/" element={<LandingPage/>}/>
          <Route path="/features" element={<FeaturesPage/>}/>
          <Route path="/pricing" element={<PricingPage/>}/>
          <Route path="/restaurant" element={<RestaurantPage/>}/>
          <Route path="/healthcare" element={<HealthcarePage/>}/>
          <Route path="/contact" element={<ContactPage/>}/>
          {/* Twilio A2P 10DLC canonical compliance URLs */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage/>}/>
          <Route path="/terms-of-service" element={<TermsOfServicePage/>}/>
          <Route path="/sms-program" element={<SmsProgramPage/>}/>
          {/* Legacy short URLs — keep them working as redirects so all
              pre-existing inbound links remain valid. */}
          <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />}/>
          <Route path="/terms" element={<Navigate to="/terms-of-service" replace />}/>
          <Route path="/login" element={<AuthErrorBoundary><LoginPage/></AuthErrorBoundary>}/>
          <Route path="/register" element={<AuthErrorBoundary><RegisterPage/></AuthErrorBoundary>}/>
          <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
          <Route path="/reset-password" element={<ResetPasswordPage/>}/>
          <Route path="/chat/:slug" element={<PublicChatbot/>}/>
          <Route path="/showcase/:slug" element={<ShowcasePage/>}/>
          {/* Phase 3D — Retail customer surfaces (public, mobile-first) */}
          {/* Phase 3L.6 — Order matters: chat/orders must be declared
              BEFORE the catch-all /retail/:slug/:linkSlug so React Router
              matches them as reserved sub-routes, not as link_slugs. */}
          <Route path="/retail/:slug" element={<RetailCustomerPage/>}/>
          <Route path="/retail/:slug/chat" element={<RetailChatPage/>}/>
          <Route path="/retail/:slug/orders/:orderCode" element={<RetailOrderStatusPage/>}/>
          {/* Rudras-AI V1.0 Commerce Engine (Mar 2026) — Stripe redirect-back handler. */}
          <Route path="/retail/:slug/payment/return" element={<RetailPaymentReturn/>}/>
          <Route path="/retail/:slug/payment/cancelled" element={<RetailPaymentReturn/>}/>
          {/* Customer Mobile App Phase 2 (Sep 2026) — additive: the Stripe
              return bounce page for the tenant-branded app, and the web
              data-deletion request page (Google Play requirement). */}
          <Route path="/retail/:slug/app-return" element={<RetailAppReturn/>}/>
          <Route path="/retail/:slug/delete-my-data" element={<RetailDeleteMyData/>}/>
          <Route path="/retail/:slug/:linkSlug" element={<RetailCustomerPage/>}/>
          {/* Hospitality H1 (Sep 2026) — hotel/motel guest discovery (public)
              and the read-only Host Partner portal (own login + token). */}
          <Route path="/dine/:hostSlug" element={<HostGuestLanding/>}/>
          <Route path="/host/login" element={<HostLoginPage/>}/>
          <Route path="/host" element={<HostDashboardPage/>}/>
          <Route path="/host/orders" element={<HostOrdersPage/>}/>
          <Route path="/host/orders/:orderId" element={<HostOrderDetailPage/>}/>
          <Route path="/host/summary" element={<HostSummaryPage/>}/>
          {/* Commerce Cleanup + KDS Sprint (Feb 2026) — Tablet-optimised
              Kitchen Display. Authenticated tenant route; the operator
              signs in once on the tablet and stays on /kitchen/:slug. */}
          <Route path="/kitchen/:slug" element={<Protected><KitchenDisplayPage/></Protected>}/>
          <Route path="/demo" element={<DemoCreatePage/>}/>
          <Route path="/try" element={<DemoCreatePage/>}/>
          <Route path="/dashboard" element={<Protected><Overview/></Protected>}/>
          <Route path="/dashboard/profile" element={<Protected><BusinessProfile/></Protected>}/>
          <Route path="/dashboard/chatbot" element={<Protected><ChatbotSettings/></Protected>}/>
          {/* Minimum Usable ICP Vertical Slice (post-Step-1B) */}
          <Route path="/dashboard/icp" element={<Protected><IcpLibraryAdminPage/></Protected>}/>
          <Route path="/dashboard/resources" element={<Protected><Resources/></Protected>}/>
          <Route path="/dashboard/leads" element={<Protected><Leads/></Protected>}/>
          {/* Phase 1 Customer Foundation (Feb 2026) — tenant Customers UI */}
          <Route path="/dashboard/reports" element={<Protected><RetailReportsPage/></Protected>}/>
          <Route path="/dashboard/reports/:reportId" element={<Protected><RetailReportViewPage/></Protected>}/>
          <Route path="/dashboard/customers" element={<Protected><CustomerListPage/></Protected>}/>
          <Route path="/dashboard/customers/:id" element={<Protected><CustomerProfilePage/></Protected>}/>
          {/* Phase 2A — Coupons + Delivery + Tax foundation */}
          <Route path="/dashboard/commerce-settings" element={<Protected><CommerceSettingsPage/></Protected>}/>
          {/* Clover App Market "Alternate Launch Path" — reuses the
              existing /company/pos-config/clover/connect endpoint and the
              existing OAuth callback; no backend changes. */}
          <Route path="/dashboard/integrations/clover/launch" element={<Protected><CloverLaunchPage/></Protected>}/>
          <Route path="/dashboard/email-logs" element={<Protected><EmailLogs/></Protected>}/>
          <Route path="/dashboard/conversations" element={<Protected><Conversations/></Protected>}/>
          <Route path="/dashboard/usage" element={<Protected><UsageBilling/></Protected>}/>
          <Route path="/dashboard/subscription" element={<Protected><Subscription/></Protected>}/>
          {/* Iter64.3 — `/dashboard/billing` alias. The sidebar label and
              Stripe success_url both refer to "billing" so we keep both
              paths live to avoid 404s during the rename transition. */}
          <Route path="/dashboard/billing" element={<Protected><Subscription/></Protected>}/>
          <Route path="/dashboard/share" element={<Protected><ShareLink/></Protected>}/>
          <Route path="/dashboard/embed" element={<Protected><EmbedWidget/></Protected>}/>
          <Route path="/dashboard/privacy" element={<Protected><Privacy/></Protected>}/>
          <Route path="/dashboard/team" element={<Protected><CustomerTeam/></Protected>}/>
          <Route path="/dashboard/security" element={<Protected><CustomerSecurity/></Protected>}/>
          {/* Phase 3D — Tenant Retail Orders dashboard. The nav item is only
              rendered for tenants with `retail_mode_enabled=true`; the route
              itself stays mounted so direct URLs work for retail tenants. */}
          <Route path="/dashboard/retail-orders" element={<Protected><RetailOrdersAdminPage/></Protected>}/>
          <Route path="/dashboard/retail-catalogs" element={<Protected><RetailCatalogsAdminPage/></Protected>}/>
          <Route path="/dashboard/retail-link-profiles" element={<Protected><RetailLinkProfilesAdminPage/></Protected>}/>
          {/* P1.4a — Tenant Shipping Admin (platform-level; gated by retail mode in sidebar) */}
          <Route path="/dashboard/shipping" element={<Protected><ShippingAdminPage/></Protected>}/>
          <Route path="/dashboard/local-delivery" element={<Protected><LocalDeliveryAdminPage/></Protected>}/>
          {/* Parcel Shipping Phase 1A CP1 — customer parcel-shipping foundation */}
          <Route path="/dashboard/parcel-shipping" element={<Protected><ParcelShippingSettingsPage/></Protected>}/>
          <Route path="/dashboard/inventory" element={<Protected><InventoryAdminPage/></Protected>}/>
          <Route path="/dashboard/messaging" element={<Protected><MessagingAdminPage/></Protected>}/>
          <Route path="/dashboard/printing" element={<Protected><PrintingAdminPage/></Protected>}/>

          {/* Customer portal alias: /app/* → /dashboard/* (target SaaS structure) */}
          <Route path="/app/*" element={<AppAliasRedirect/>}/>

          {/* Internal admin portal — fully isolated from customer auth & routing */}
          <Route path="/internal-admin/login" element={<AuthErrorBoundary><InternalLoginPage/></AuthErrorBoundary>}/>
          <Route path="/internal-admin/forgot-password" element={<ForgotPasswordPage/>}/>
          <Route path="/internal-admin/reset-password" element={<InternalResetPasswordPage/>}/>
          <Route path="/internal-admin" element={<InternalProtected><InternalOverview/></InternalProtected>}/>
          <Route path="/internal-admin/tenants" element={<InternalProtected><InternalTenants/></InternalProtected>}/>
          <Route path="/internal-admin/operations" element={<InternalProtected><InternalOperationsConsole/></InternalProtected>}/>
          <Route path="/internal-admin/diagnostics" element={<InternalProtected><InternalDiagnosticsPage/></InternalProtected>}/>
          <Route path="/internal-admin/leads" element={<InternalProtected><InternalLeads/></InternalProtected>}/>
          <Route path="/internal-admin/sessions" element={<InternalProtected><InternalSessions/></InternalProtected>}/>
          <Route path="/internal-admin/escalations" element={<InternalProtected><InternalEscalations/></InternalProtected>}/>
          <Route path="/internal-admin/test-checklist" element={<InternalProtected><InternalTestChecklist/></InternalProtected>}/>
          <Route path="/internal-admin/brain-trace" element={<InternalProtected><InternalBrainTrace/></InternalProtected>}/>
          <Route path="/internal-admin/demo-jobs" element={<InternalProtected><InternalDemoJobs/></InternalProtected>}/>
          <Route path="/internal-admin/email-logs" element={<InternalProtected><InternalEmailLogs/></InternalProtected>}/>
          {/* PILOT-1B Sub-sprint B — Super-Admin OTP Diagnostics. */}
          <Route path="/internal-admin/otp-diagnostics" element={<InternalProtected><InternalOtpDiagnostics/></InternalProtected>}/>
          <Route path="/internal-admin/security" element={<InternalProtected><InternalSecurity/></InternalProtected>}/>
          {/* Super-admin controlled Uber Direct TEST dispatch console — replaces
              the retired Postman-based operator procedure. Page-level access
              is narrowed to super_admin inside the component itself; every
              action it takes is independently super_admin-enforced by the
              backend regardless of this page-level check. */}
          <Route path="/internal-admin/uber-test-dispatch" element={<InternalProtected><InternalUberTestDispatchConsole/></InternalProtected>}/>
          <Route path="/internal-admin/uber-production" element={<InternalProtected><InternalUberProductionOnboarding/></InternalProtected>}/>
          <Route path="/internal-admin/master-product-images" element={<InternalProtected><InternalMasterProductImages/></InternalProtected>}/>

          {/* Legacy redirect — /super was the old single super-admin page */}
          <Route path="/super" element={<SuperLegacyRedirect/>}/>

          {/* 404 catch-all — must remain LAST. Renders the friendly NotFoundPage. */}
          <Route path="*" element={<NotFoundPage/>}/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
